/*
 * =============================================================================
 * App.js — Root Component for the Evolunic EIS Agent Platform (Web Frontend)
 * =============================================================================
 *
 * ROLE:
 *   This file defines three things:
 *     1. `TabPanel`  — Generic MUI tab panel wrapper (currently used only by convention).
 *     2. `App`       — Top-level provider tree and route definitions.
 *     3. `AppContent` — The authenticated shell: header, sidebar nav, section views,
 *                        chat panel, and activity panel.
 *
 * KEY DEPENDENCIES:
 *   - React Router (BrowserRouter) — client-side routing for auth pages + main app
 *   - MUI (Material UI)            — all visual components and theming
 *   - AuthContext (useAuth)         — provides `user`, `logout`, `isDevMode`
 *   - ActivityContext (useActivity) — provides `addActivity`, `triggerRefresh`
 *   - LanguageContext               — i18n support via `useLanguage`
 *   - ThemeContext                  — dark/light mode toggle
 *   - `api` service (./services/api) — all backend HTTP calls
 *
 * DATA FLOW SUMMARY:
 *   AuthContext.user  -->  currentUser (local mirror)  -->  child components
 *   api.getProjects() -->  allProjects                 -->  ChipStrip (ProjectChip), ProjectDashboard
 *   api.getBranches() -->  branches                    -->  ProjectView, engineering views
 *   selectedProject   -->  projectId prop to all section views + ChatContainer context
 *   currentBranch     -->  branchId prop to all section views + ChatContainer context
 *   sessionId         -->  ChatContainer, CADViewerView (links chat messages to a session)
 *
 * localStorage KEYS (managed in this file):
 *   'eis_session_id'               — persists chat session ID across page reloads
 *   'eis_session_user'             — user ID tied to the session (security: invalidates on user switch)
 *   'eis_activeSection'            — last active sidebar section (e.g. 'projects', 'cad')
 *   'eis_selectedProjectId'        — last selected project ID for restore on reload
 *   'recentProjects'               — JSON array of up to 5 recent project IDs for quick switcher
 *   'branch_<projectId>'           — last selected branch per project (dynamic key)
 *
 *   WARNING: ErrorBoundary (./components/common/ErrorBoundary.js) also resets
 *   'eis_activeSection' to 'projects' on crash recovery. Changing that key name
 *   here requires updating ErrorBoundary too.
 *
 *   WARNING: LegalPage references 'eis_session_id' in its cookie/storage disclosure.
 *   Renaming that key requires updating LegalPage as well.
 *
 * SIDE-EFFECT NOTES:
 *   - Changing the provider nesting order in `App` can break context access.
 *     AuthProvider must be inside Router (it uses useNavigate internally).
 *   - `selectedProject` drives both the project detail view AND the projectId
 *     passed to every engineering view. Setting it to null clears project scope
 *     for CAD, documents, and requirements.
 *   - `currentBranch` is persisted per-project. Changing the storage key format
 *     ('branch_<projectId>') will orphan saved preferences.
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  AppBar,
  Breadcrumbs,
  Link,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Fab,
} from '@mui/material';
import { ArrowBack, AutoAwesome, ChevronRight } from '@mui/icons-material';
import ChatContainer from './components/chat/ChatContainer';
import GlobalNavigation from './components/layout/GlobalNavigation';
import BottomActivityPanel from './components/layout/BottomActivityPanel';
import MenuBar from './components/layout/MenuBar';
import ChipStrip from './components/layout/ChipStrip';
import CadRibbon from './components/layout/CadRibbon';
import EcadRibbon from './components/layout/EcadRibbon';
import WhatChangedDialog from './components/cad/WhatChangedDialog';
import KeyboardShortcutsDialog from './components/layout/dialogs/KeyboardShortcutsDialog';
import AboutDialog from './components/layout/dialogs/AboutDialog';
import { ReleaseProvider } from './context/ReleaseContext';
import { api } from './services/api';
import { ActivityProvider, useActivity } from './context/ActivityContext';
import { NotificationProvider } from './context/NotificationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SettingsPage from './pages/SettingsPage';
import LegalPage from './pages/LegalPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ChangelogPage from './pages/ChangelogPage';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import { TourProvider, useTour } from './components/help/TourProvider';
import WelcomeDialog from './components/onboarding/WelcomeDialog';
import OnboardingChecklist from './components/onboarding/OnboardingChecklist';
import SpotlightGuide from './components/onboarding/SpotlightGuide';
import ProjectTutorial from './components/onboarding/ProjectTutorial';
import { OnboardingProvider } from './context/OnboardingContext';
import { JobPollerProvider } from './context/JobPollerContext';
import { BillingProvider } from './context/BillingContext';
import { UploadProvider } from './context/UploadContext';
import GlobalUploadPanel from './components/shared/GlobalUploadPanel';
import { IndustryProfileProvider, useIndustryProfile } from './context/IndustryProfileContext';
import PaywallPrompt from './components/billing/PaywallPrompt';
import BillingReturnPage from './components/billing/BillingReturnPage';
import { initPostHog, identifyUser, resetUser, trackEvent } from './analytics/posthog';

// Lazy-loaded section views — reduces initial bundle by ~60%
const ProjectDashboard = lazy(() => import('./components/dashboard/ProjectDashboard'));
const ProjectView = lazy(() => import('./components/projects/ProjectView'));
const UnifiedCADView = lazy(() => import('./components/cad/UnifiedCADView'));
const ElectronicsView = lazy(() => import('./components/electronics/ElectronicsView'));
const DocumentsView = lazy(() => import('./components/engineering/DocumentsView'));
const RequirementsView = lazy(() => import('./components/engineering/RequirementsView'));
const OrchestratorView = lazy(() => import('./components/orchestrator/OrchestratorView'));
const CADViewerView = lazy(() => import('./components/cad-agent/CADViewerView'));
const ComplianceView = lazy(() => import('./components/compliance/ComplianceView'));
const ValidationFindingsView = lazy(() => import('./components/validation/ValidationFindingsView'));
const VVRunnerView = lazy(() => import('./components/vv-runner/VVRunnerView'));
const QualityManagementView = lazy(() => import('./components/quality-events/QualityManagementView'));
const DeclarationOfConformityView = lazy(() => import('./components/doc/DeclarationOfConformityView'));
const KnowledgeBaseView = lazy(() => import('./components/knowledge-base/KnowledgeBaseView'));
const ChangeImpactView = lazy(() => import('./components/knowledge-graph/ChangeImpactView'));
const PLCalculatorView = lazy(() => import('./components/pl-calculator/PLCalculatorView'));
const AnalyticsView = lazy(() => import('./components/analytics/AnalyticsView'));
const IntegrationsView = lazy(() => import('./components/integrations/IntegrationsView'));

/*
 * TabPanel — Generic accessible tab panel wrapper.
 * Only renders children when `value === index` to avoid mounting hidden content.
 * Currently referenced by convention; not actively used in AppContent's
 * section-based routing (which uses `activeSection` string matching instead).
 */
// eslint-disable-next-line no-unused-vars -- retained (WIP)
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

/*
 * App — Root component. Builds the provider tree and defines top-level routes.
 *
 * Provider nesting order matters:
 *   ErrorBoundary > ThemeProvider > LanguageProvider > Router > AuthProvider > ...
 *
 * WARNING: AuthProvider MUST be inside Router because it calls useNavigate()
 * internally to redirect on login/logout. Moving it outside Router will crash.
 *
 * WARNING: ActivityProvider wraps all routes so that both protected and public
 * pages can potentially log activity. CookieConsentBanner is rendered outside
 * Routes so it appears on every page including auth pages.
 */
function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
      <LanguageProvider>
        <Router>
          <AuthProvider>
            <BillingProvider>
            <IndustryProfileProvider>
            <NotificationProvider>
              <UploadProvider>
              <ActivityProvider>
              <TourProvider>
              <OnboardingProvider>
                <PaywallPrompt />
                <Routes>
                  {/* --- Public routes (no auth required) --- */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/legal" element={<LegalPage />} />

                  {/* --- Protected routes (redirect to /login if not authenticated) --- */}
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                  <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
                  <Route path="/changelog" element={<ProtectedRoute><ChangelogPage /></ProtectedRoute>} />

                  {/* Stripe redirects back here after Checkout. The component
                      triggers a BillingContext refresh + toast, then navigates
                      to `/` — avoiding a dead URL the user doesn't understand. */}
                  <Route path="/billing/success" element={<ProtectedRoute><BillingReturnPage type="success" /></ProtectedRoute>} />
                  <Route path="/billing/cancel" element={<ProtectedRoute><BillingReturnPage type="cancel" /></ProtectedRoute>} />

                  {/*
                   * Catch-all: any path not matched above lands in AppContent.
                   * AppContent handles its own internal "routing" via activeSection state
                   * rather than URL-based routing, so all authenticated pages share one URL.
                   * Side-effect: browser back/forward does NOT navigate between sections.
                   */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AppContent />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
                {/* Cookie consent banner shown on all pages (public + protected) */}
                <CookieConsentBanner />
              </OnboardingProvider>
              </TourProvider>
              </ActivityProvider>
              </UploadProvider>
            </NotificationProvider>
            </IndustryProfileProvider>
            </BillingProvider>
          </AuthProvider>
        </Router>
      </LanguageProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

/*
 * AppContent — The main authenticated application shell.
 *
 * Responsible for:
 *   - Session management (sessionId generation + localStorage persistence)
 *   - Project and branch selection (global context for all engineering views)
 *   - Section-based navigation (sidebar drives activeSection state)
 *   - Layout: fixed header, left sidebar (GlobalNavigation), main content, chat sidebar
 *   - User menu (settings, sign out)
 *
 * This component is the single source of truth for:
 *   - selectedProject (which project is active)
 *   - currentBranch   (which branch within that project)
 *   - activeSection   (which sidebar section is displayed)
 * All child views receive these as props; they do NOT manage their own project/branch state.
 */
function AppContent() {
  /* --- Core state --- */
  const [sessionId, setSessionId] = useState('');     // Unique session ID for chat; persisted in localStorage
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const [activeTab, setActiveTab] = useState(0);      // Tab index (legacy; not actively driving UI — see activeSection)
  const [navRequestedTabs, setNavRequestedTabs] = useState({});  // Per-section initial tab from sidebar sub-tab clicks
  const [navRequestedSubTabs, setNavRequestedSubTabs] = useState({});  // Per-section initial sub-tab (menubar deep-links, e.g. Reports→Audit Package)
  const [createSignals, setCreateSignals] = useState({});  // Per-section create-dialog trigger (Create menu → section pops its create dialog)
  const [complianceFocus, setComplianceFocus] = useState(null);  // Deep-link focus into the Compliance board (e.g. from a change record's affected entity)
  const [uploadedFiles, setUploadedFiles] = useState([]); // Accumulated uploaded files passed to ChatContainer
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const { addActivity, triggerRefresh, refreshTrigger } = useActivity();
  const { user, logout, isDevMode } = useAuth();      // `user` is the auth source of truth from AuthContext
  const navigate = useNavigate();

  // Header dialog state (Keyboard Shortcuts, About — opened from menubar)
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  // "What Changed?" dialog — driven by the CAD ribbon's Compare-group button.
  const [whatChangedDrawing, setWhatChangedDrawing] = useState(null);

  // Chat sidebar collapse — persisted across sessions. Collapsing it gives
  // the main content (esp. the CAD viewer) ~430px of extra horizontal space.
  // The thin collapsed rail keeps the affordance discoverable.
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try { return localStorage.getItem('eis_chat_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('eis_chat_collapsed', chatCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [chatCollapsed]);

  // Left navigation collapse — collapsed rail is 60px, expanded is 260px.
  // Reclaims ~200px for the main content (CAD viewer benefits most).
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem('eis_nav_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('eis_nav_collapsed', navCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [navCollapsed]);

  /*
   * --- Project Management State ---
   * `currentUser` is a local mirror of AuthContext's `user`, reshaped for child components.
   * `selectedProject` is the currently active project object (null = no project selected).
   * `users` is the full user list from backend, used for project member assignment UI.
   *
   * WARNING: `currentUser` is derived from `user` via useEffect. There is a brief
   * render cycle where `currentUser` is null even though `user` exists. Child components
   * must handle null currentUser gracefully.
   */
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  // Dual-mode (2026-07-16) — the active project's vertical profile drives nav
  // vocabulary + the mode chip. Safe for a null project (returns default vocab).
  const { vocab: navVocab, modeSlug, modeName } = useIndustryProfile(selectedProject?.project_id);
  const [users, setUsers] = useState([]);
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [activeDrawingId, setActiveDrawingId] = useState(null);
  // CAD ribbon needs the full selected drawing object (not just the id) to
  // pass through to action handlers. Lifted from UnifiedCADView via
  // onActiveDrawingChange callback below.
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const [cadSelectedDrawing, setCadSelectedDrawing] = useState(null);
  // selectedFile from the CAD file list — this is the canonical "what is
  // the user working with" surface. CADViewerArea's dropdown is just a
  // render target. Ribbon actions read this so clicking a file in the list
  // and then a ribbon button operates on that file, not whatever happens
  // to be rendered in the 3D viewer.
  const [cadSelectedFile, setCadSelectedFile] = useState(null);
  const [cadSelectedFileIds, setCadSelectedFileIds] = useState([]);
  // Electronics (ECAD) section — the ribbon acts on the selected ECAD design.
  const [electronicsSelectedFile, setElectronicsSelectedFile] = useState(null);

  // KG focus request — set when ImpactBanner's "Open in KG" is clicked from any view.
  // Cleared after KnowledgeGraphView picks it up to avoid re-triggering on re-renders.
  const [kgFocusRequest, setKgFocusRequest] = useState(null);

  // Initialize PostHog analytics on mount
  useEffect(() => {
    initPostHog();
  }, []);

  // Tour navigation hook — registration happens after handleSectionChange is defined (below)
  const { setNavigateToSection, startTour } = useTour();

  /*
   * Sync currentUser with AuthContext's user object.
   * This reshapes the auth user into the {id, name, role, email} format
   * expected by ProjectDashboard, ProjectView, and other child components.
   * Runs whenever the auth user changes (login, token refresh, etc.).
   */
  useEffect(() => {
    if (user) {
      setCurrentUser({
        id: user.user_id,
        name: user.username,
        role: user.role,
        email: user.email,
      });
      identifyUser(user);
    }
  }, [user]);

  // Track newly created drawings so the chat context includes the drawing_id
  // even before the CAD tab mounts or when the user is on a different tab.
  useEffect(() => {
    const handler = (e) => setActiveDrawingId(e.detail?.drawingId || null);
    window.addEventListener('cadDrawingCreated', handler);
    return () => window.removeEventListener('cadDrawingCreated', handler);
  }, []);

  // e-pilot navigation: when a tool creates/queries something, switch to the right section + tab
  useEffect(() => {
    const handler = (e) => {
      const { section, tab, entityId, entityType } = e.detail || {};
      if (section) {
        setActiveSection(section);
        localStorage.setItem('eis_activeSection', section);
        // Dispatch tab change for the target section to pick up
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('epilotTabChange', { detail: { tab, entityId, entityType } }));
        }, 300); // Wait for section to mount
      }
    };
    window.addEventListener('epilotNavigate', handler);
    return () => window.removeEventListener('epilotNavigate', handler);
  }, []);

  /*
   * Fetch all users from the backend for project member assignment dropdowns.
   * Array.isArray guard: the API may return non-array data on error or unexpected
   * response shape; defaulting to [] prevents downstream .map() crashes.
   * Fallback: if the API call fails entirely, include at least the current user
   * so the UI isn't completely empty.
   */
  useEffect(() => {
    // No team concept — only include the current user for project owner dropdown.
    if (user) {
      setUsers([{
        id: user.user_id,
        name: user.username,
        role: user.role,
        email: user.email,
      }]);
    }
  }, [user]);

  /*
   * Fetch user's selected software preferences for the nav sidebar.
   * Synced with the SoftwareSelectionModal in OrchestratorView.
   */
  useEffect(() => {
    const fetchSoftwarePrefs = async () => {
      try {
        const response = await api.getUserProfile();
        setSelectedSoftware(response.data?.selected_software ?? null);
      } catch (err) {
        console.error('Failed to fetch software preferences:', err);
      }
    };
    if (user) {
      fetchSoftwarePrefs();
    }
  }, [user]);

  /*
   * Listen for software preference updates from OrchestratorView's modal.
   * When the user saves a new selection, we update the nav sidebar in sync.
   */
  useEffect(() => {
    const handlePrefsUpdated = (e) => {
      setSelectedSoftware(e.detail);
    };
    window.addEventListener('softwarePreferencesUpdated', handlePrefsUpdated);
    return () => window.removeEventListener('softwarePreferencesUpdated', handlePrefsUpdated);
  }, []);

  /*
   * --- Navigation State ---
   * `activeSection` determines which main content view is rendered.
   * Initialized from localStorage so the user returns to their last section on reload.
   * Valid values: 'projects', 'cad', 'documents',
   *               'requirements', 'orchestrator', 'cadviewer'
   *
   * WARNING: Changing the key 'eis_activeSection' requires updating:
   *   - ErrorBoundary.js (resets it to 'projects' on crash)
   *   - Any other code referencing this key
   */
  const [activeSection, setActiveSection] = useState(() => {
    const saved = localStorage.getItem('eis_activeSection');
    // Fallback removed sections to 'projects'
    const removedSections = ['parts', 'simulations', 'pl-calculator', 'validation', 'vv-overview', 'dhr', 'capa', 'post-market', 'conformance', 'fault-tree', 'iq-oq-pq', 'data-mapper'];
    if (saved && removedSections.includes(saved)) return 'projects';
    return saved || 'projects';
  });
  const [currentBranch, setCurrentBranch] = useState(null);  // Branch ID (not name); null until API loads
  const [branches, setBranches] = useState([]);                // Full branch objects from api.getBranches()
  // Header Switcher State — allProjects feeds the ChipStrip's ProjectChip dropdown
  const [allProjects, setAllProjects] = useState([]);
  const [recentProjectIds, setRecentProjectIds] = useState([]); // Up to 5 most recently visited project IDs

  // Auto-open the most recently created project when the tutorial starts
  // (must be after allProjects declaration to avoid temporal dead zone)
  useEffect(() => {
    const handler = () => {
      if (allProjects.length > 0) {
        const sorted = [...allProjects].sort((a, b) =>
          new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        setSelectedProject(sorted[0]);
      }
    };
    window.addEventListener('tutorial:auto-open-project', handler);
    return () => window.removeEventListener('tutorial:auto-open-project', handler);
  }, [allProjects]);

  /**
   * Refresh the allProjects list from the API.
   * Called directly by ProjectDashboard after create/delete, and also
   * by the useEffect on refreshTrigger as a fallback.
   */
  const refreshAllProjects = async () => {
    try {
      const response = await api.getProjects();
      const projects = Array.isArray(response.data) ? response.data : [];
      setAllProjects(projects);

      // Clean up recentProjectIds — remove any IDs for deleted projects
      const projectIdSet = new Set(projects.map(p => p.project_id));
      setRecentProjectIds(prev => {
        const cleaned = prev.filter(id => projectIdSet.has(id));
        if (cleaned.length !== prev.length) {
          localStorage.setItem('recentProjects', JSON.stringify(cleaned));
        }
        return cleaned;
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };


  /*
   * Session ID persistence — generates and stores a unique session identifier.
   *
   * Security behavior: if a different user logs in on the same browser, the old
   * session is discarded and a new one is generated. This prevents session leakage
   * between users sharing a machine.
   *
   * localStorage keys used:
   *   'eis_session_id'   — the session ID string
   *   'eis_session_user' — the user_id that owns this session
   *
   * WARNING: sessionId is passed to ChatContainer and CADViewerView. Changing
   * the generation format may affect backend session lookup/matching.
   */
  useEffect(() => {
    const STORAGE_KEY = 'eis_session_id';
    const USER_KEY = 'eis_session_user';

    const currentUserId = user?.user_id || null;
    const storedUserId = localStorage.getItem(USER_KEY);
    let storedSessionId = localStorage.getItem(STORAGE_KEY);

    // Generate new session if: no session exists, or different user logged in
    if (!storedSessionId || (currentUserId && storedUserId !== currentUserId)) {
      storedSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(STORAGE_KEY, storedSessionId);
      if (currentUserId) {
        localStorage.setItem(USER_KEY, currentUserId);
      }
    }

    setSessionId(storedSessionId);
  }, [user?.user_id]);

  // Listen for chat session changes (e.g. "New Chat" button in ChatContainer)
  useEffect(() => {
    const handler = (e) => {
      const newId = e.detail?.sessionId;
      if (newId) setSessionId(newId);
    };
    window.addEventListener('chatSessionChanged', handler);
    return () => window.removeEventListener('chatSessionChanged', handler);
  }, []);

  /*
   * Fetch all projects on mount for the header project switcher.
   * Also restores the last selected project from localStorage so the user
   * returns to their previous project context on page reload.
   *
   * Array.isArray guard: protects against unexpected API response shapes.
   *
   * Also loads the recent projects list from localStorage for the quick-switch menu.
   *
   * Re-fetches whenever refreshTrigger changes (e.g. after project create/delete
   * in ProjectDashboard calls triggerRefresh()). Also loads recent project IDs
   * from localStorage on initial mount.
   */
  useEffect(() => {
    // Fetch projects and restore saved state on mount
    const init = async () => {
      await refreshAllProjects();

      // Restore last selected project from localStorage (initial mount only)
      const savedProjectId = localStorage.getItem('eis_selectedProjectId');
      if (savedProjectId) {
        const response = await api.getProjects();
        const projects = Array.isArray(response.data) ? response.data : [];
        const saved = projects.find(p => p.project_id === savedProjectId);
        if (saved) {
          setSelectedProject(saved);
        }
      }
    };
    init();

    // Load recent project IDs from localStorage
    const saved = localStorage.getItem('recentProjects');
    if (saved) {
      try {
        setRecentProjectIds(JSON.parse(saved));
      } catch (e) {
        console.error('Error parsing recent projects:', e);
      }
    }
  }, []);

  /*
   * Update the recent projects list (most recently used first, max 5).
   * Persists to localStorage key 'recentProjects' as a JSON array of project IDs.
   * Consumed by ChipStrip's ProjectChip to show a "Recent" section.
   */
  const updateRecentProjects = (projectId) => {
    setRecentProjectIds(prev => {
      const updated = [projectId, ...prev.filter(id => id !== projectId)].slice(0, 5);
      localStorage.setItem('recentProjects', JSON.stringify(updated));
      return updated;
    });
  };

  /*
   * Handle project selection changes from the header switcher or project dashboard.
   * Updates selectedProject state AND persists the choice to localStorage.
   *
   * Side-effects:
   *   - Setting a project triggers the refreshBranches effect (via selectedProject dep).
   *   - All engineering views will re-render with the new projectId.
   *   - ChatContainer context object updates with the new project_id.
   *   - Clearing the project (null) removes the localStorage entry.
   */
  const handleProjectChange = (project) => {
    setSelectedProject(project);
    if (project) {
      updateRecentProjects(project.project_id);
      localStorage.setItem('eis_selectedProjectId', project.project_id);
      trackEvent('project_selected', { project_id: project.project_id, project_name: project.name });
    } else {
      localStorage.removeItem('eis_selectedProjectId');
    }
  };

  /*
   * Global search — onPick handler. Routes search hits to the right section.
   * v1: switches sections only; doesn't auto-open the entity's detail drawer.
   * That deep-link wiring is a follow-up (would need each section to expose a
   * setSelectedEntity API from props/context).
   */
  const handleSearchPick = useCallback((hit) => {
    if (!hit) return;
    switch (hit.entity_type) {
      case 'project': {
        const target = allProjects.find((p) => p.project_id === hit.entity_id);
        if (target) handleProjectChange(target);
        setActiveSection('projects');
        break;
      }
      case 'document':
        setActiveSection('documents');
        break;
      case 'requirement':
        setActiveSection('requirements');
        break;
      case 'cad_drawing':
      case 'bom_line':
        setActiveSection('cad');
        break;
      case 'compliance_standard':
        setActiveSection('compliance');
        break;
      default:
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProjects]);

  /*
   * Ctrl/⌘+F → dispatch eis:open-search so SearchChip opens its popover.
   * Skipped when focus is inside an editable field — native Find still works
   * for users typing into text inputs / textareas / contenteditable surfaces.
   */
  useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const handler = (e) => {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (!(e.metaKey || e.ctrlKey)) return;
      if (isEditable(document.activeElement)) return;
      e.preventDefault();
      window.dispatchEvent(new CustomEvent('eis:open-search'));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);


  /*
   * Fetch branches for the currently selected project.
   * Called whenever selectedProject changes (via useEffect below).
   *
   * Branch selection priority:
   *   1. Saved branch from localStorage ('branch_<projectId>') if it still exists
   *   2. Current branch if it still exists in the new branch list
   *   3. The 'main' branch (by name)
   *   4. First branch in the list
   *
   * Array.isArray guard: protects against unexpected API response shapes.
   *
   * WARNING: This function reads `currentBranch` from closure at call time.
   * If called multiple times rapidly, stale closure values could cause incorrect fallback.
   */
  const refreshBranches = async () => {
    if (selectedProject) {
      try {
        const response = await api.getBranches(selectedProject.project_id);
        // Guard: ensure branchData is always an array
        const branchData = Array.isArray(response.data) ? response.data : [];
        setBranches(branchData);

        // Check for saved branch preference in localStorage (per-project key)
        const savedBranch = localStorage.getItem(`branch_${selectedProject.project_id}`);

        // Only set default if not already set or invalid
        // If we have a saved branch and it exists in the response, use it.
        if (savedBranch && branchData.find(b => b.branch_id === savedBranch)) {
            setCurrentBranch(savedBranch);
        } else if (!currentBranch || !branchData.find(b => b.branch_id === currentBranch)) {
             // Fallback: prefer 'dev' branch (writable), then first non-main, then main
             const devBranch = branchData.find(b => b.name === 'dev');
             const nonMainBranch = branchData.find(b => b.name !== 'main');
             if (devBranch) {
               setCurrentBranch(devBranch.branch_id);
             } else if (nonMainBranch) {
               setCurrentBranch(nonMainBranch.branch_id);
             } else if (branchData.length > 0) {
               setCurrentBranch(branchData[0].branch_id);
             }
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      }
    } else {
      // No project selected — reset branch state to defaults
      setBranches([]);
      setCurrentBranch(null);
    }
  };

  /*
   * Persist the current branch selection to localStorage whenever it changes.
   * Uses a dynamic key 'branch_<projectId>' so each project remembers its own branch.
   * WARNING: Changing this key format orphans previously saved preferences.
   */
  useEffect(() => {
      if (selectedProject && currentBranch) {
          localStorage.setItem(`branch_${selectedProject.project_id}`, currentBranch);
      }
  }, [currentBranch, selectedProject]);

  /*
   * Build breadcrumb trail for the current navigation state.
   * The first crumb is always "Evolunic" (resets to project list on click).
   * For the 'projects' section, it shows: Evolunic > Projects > [Project Name].
   * For other sections, it shows: Evolunic > [Section Label].
   */
  const getBreadcrumbs = () => {
    const crumbs = [
      { label: 'EIS', onClick: () => { setActiveSection('projects'); setSelectedProject(null); } }
    ];

    const sectionLabels = {
      projects: 'Products',
      cad: 'CAD',
      documents: 'Documents',
      requirements: 'Requirements',
      compliance: 'Compliance',
      'declaration-of-conformity': 'Declaration of Conformity',
      vv: 'V&V Runner',
      'quality-events': 'Quality Management',
      analytics: 'Analytics',
      'knowledge-base': 'Knowledge Base',
      'knowledge-graph': 'Change Impact',
      orchestrator: 'Workflows',
      cadviewer: 'CAD Viewer',
    };

    if (activeSection === 'projects') {
      crumbs.push({
        label: 'Products',
        onClick: () => { setSelectedProject(null); }
      });

      if (selectedProject) {
        crumbs.push({ label: selectedProject.name, onClick: null });
      }
    } else {
      crumbs.push({ label: sectionLabels[activeSection] || activeSection, onClick: null });
    }

    return crumbs;
  };

  /* Trigger branch fetch whenever the selected project changes */
  useEffect(() => {
    refreshBranches();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally limited
  }, [selectedProject]);

  /*
   * Handle file upload — appends to the uploadedFiles array.
   * These files are passed to ChatContainer so the AI chat has context about uploads.
   * NOTE: This only accumulates; there is no mechanism to remove files from the list.
   */
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const handleFileUploaded = (file) => {
    setUploadedFiles(prev => [...prev, file]);
    // Could trigger a refresh of search results or other actions
  };

  /* Legacy tab change handler — kept for TabPanel compatibility */
  // eslint-disable-next-line no-unused-vars -- retained (WIP)
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  /*
   * Handle sidebar section navigation.
   * Persists the active section to localStorage for restore on reload.
   *
   * Side-effect: Navigating to 'projects' clears the selected project.
   * This means switching to another section and back to 'projects' will
   * deselect the current project. This is intentional — the projects section
   * shows the dashboard list view when no project is selected.
   */
  const handleSectionChange = (section, tabIndex = 0, subTab = 0, focus = null) => {
    setActiveSection(section);
    setNavRequestedTabs(prev => ({ ...prev, [section]: tabIndex }));
    setNavRequestedSubTabs(prev => ({ ...prev, [section]: subTab }));
    // Deep-link focus (e.g. a change record's affected entity → Compliance board
    // filtered to that entity). nonce forces the target view to re-apply it.
    if (section === 'compliance' && focus?.entityId) {
      setComplianceFocus({ entityType: focus.entityType, entityId: focus.entityId, nonce: Date.now() });
    }
    localStorage.setItem('eis_activeSection', section);
    trackEvent('section_viewed', { section });
  };

  /*
   * Create menu — navigate to the owning section then bump its create-signal
   * so the section pops its existing create dialog. Signal is a per-section
   * incrementing nonce; each view opens on change (see its createSignal effect).
   */
  const requestCreate = (section, tabIndex = 0) => {
    handleSectionChange(section, tabIndex);
    setCreateSignals(prev => ({ ...prev, [section]: (prev[section] || 0) + 1 }));
  };

  const handleCreate = (kind) => {
    switch (kind) {
      case 'product':     goToProjectsList(); break;
      case 'requirement': requestCreate('requirements', 0); break;
      case 'standard':    requestCreate('compliance', 0); break;
      case 'session':     requestCreate('vv', 0); break;
      case 'document':    requestCreate('documents', 0); break;
      default: break;
    }
  };

  // Register section navigation callback so guided tours can switch views
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNavigateToSection(handleSectionChange); }, []);

  // eis:kg-focus-entity — dispatched by ImpactBanner when the user clicks
  // "Open in KG". Navigate to the KG section and hand the entity ref to
  // KnowledgeGraphView via kgFocusRequest so it can pre-focus on mount.
  useEffect(() => {
    const handler = (e) => {
      const { entityType, entityId, impactNodes } = e.detail || {};
      if (!entityType || !entityId) return;
      setKgFocusRequest({ type: entityType, id: entityId, impactNodes: impactNodes || [] });
      handleSectionChange('knowledge-graph');
    };
    window.addEventListener('eis:kg-focus-entity', handler);
    return () => window.removeEventListener('eis:kg-focus-entity', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Menubar action handlers — most surface where things live by navigating
  // to the section that owns them. Phase 2 wires deeper into modal dialogs.
  const goToProjectsList = () => { setSelectedProject(null); handleSectionChange('projects'); };
  const goToVersionControl = () => { handleSectionChange('projects'); };

  /* ========================= RENDER ========================= */
  return (
    <ReleaseProvider>
    <JobPollerProvider projectId={selectedProject?.project_id}>
      {/* Outer flex container: sidebar (GlobalNavigation) + main content area */}
      <Box sx={{ display: 'flex' }}>

        {/* --- Fixed two-row header: MenuBar (Row 1) + ChipStrip (Row 2) --- */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (t) => t.zIndex.drawer + 1,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <MenuBar
            hasProject={!!selectedProject}
            hasBranch={!!currentBranch}
            onSectionChange={handleSectionChange}
            onVersionControl={goToVersionControl}
            onNewProject={goToProjectsList}
            onOpenProject={goToProjectsList}
            onProperties={() => handleSectionChange('projects')}
            onSignOut={() => { resetUser(); logout(); }}
            onCreate={handleCreate}
            onToggleChat={() => setChatCollapsed((v) => !v)}
            onOpenApprovals={() => window.dispatchEvent(new CustomEvent('eis:open-approvals'))}
            onRefresh={() => window.location.reload()}
            onToggleFullscreen={() => {
              if (document.fullscreenElement) document.exitFullscreen();
              else document.documentElement.requestFullscreen?.();
            }}
            onHelpCenter={() => navigate('/help')}
            onStartTour={() => startTour?.('welcome')}
            onKeyboardShortcuts={() => setShortcutsOpen(true)}
            onAbout={() => setAboutOpen(true)}
          />
          <ChipStrip
            projects={allProjects}
            selectedProject={selectedProject}
            onProjectChange={handleProjectChange}
            branches={branches}
            currentBranch={currentBranch}
            onBranchChange={setCurrentBranch}
            recentProjectIds={recentProjectIds}
            user={user}
            currentUser={currentUser}
            isDevMode={isDevMode}
            onStagedClick={goToVersionControl}
            onProfileSettings={() => navigate('/settings')}
            onSignOut={() => { resetUser(); logout(); }}
            onSearchPick={handleSearchPick}
            modeSlug={modeSlug}
            modeName={modeName}
          />
        </AppBar>

        {/*
         * Left sidebar navigation.
         * Drives activeSection state which determines which main content view renders.
         * WARNING: Adding a new section here requires also adding a corresponding
         * conditional render block in the main content area below and a label
         * in getBreadcrumbs().sectionLabels.
         */}
        <GlobalNavigation
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          collapsed={navCollapsed}
          onToggleCollapsed={() => setNavCollapsed((v) => !v)}
          vocab={navVocab}
        />

        {/* --- Main Content Area --- */}
        {/* mt: 10 offsets below the fixed two-row header (MenuBar 32px + ChipStrip 44px) */}
        <Box component="main" sx={{ flexGrow: 1, mt: 10 }}>
          {/* Section-specific ribbon. Today only the CAD section has one;
              other sections can add their own ribbon (or none) later. The
              ribbon is sticky directly below the fixed header so it stays
              visible when the content scrolls. */}
          {activeSection === 'cad' && (
            <Box sx={{ position: 'sticky', top: 80, zIndex: 1100 }}>
              <CadRibbon
                // Single-action context comes from the file list (canonical),
                // not the 3D viewer dropdown. UnifiedCADView's listener
                // resolves file → drawing via the same name/s3/source-doc
                // match used by findMatchingDrawing.
                selectedFile={cadSelectedFile}
                selectedFileIds={cadSelectedFileIds}
                onExtractBOM={(file, kind = '2d') => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action', {
                    detail: { file_id: file?.id, action: `extract-${kind}` },
                  }));
                }}
                onViewBOM={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action', {
                    detail: { file_id: file?.id, action: 'view' },
                  }));
                }}
                onUploadXLSX={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action', {
                    detail: { file_id: file?.id, action: 'upload-xlsx' },
                  }));
                }}
                onReconcile={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action', {
                    detail: { file_id: file?.id, action: 'reconcile' },
                  }));
                }}
                onWhatChanged={(file) => setWhatChangedDrawing(file)}
                onLink={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:link-entity', {
                    detail: { file_id: file?.id },
                  }));
                }}
                onSuggestLinks={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:suggest-links', {
                    detail: { file_id: file?.id },
                  }));
                }}
                onNewVersion={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:cad:new-version', {
                    detail: { file_id: file?.id },
                  }));
                }}
                onOpenDashboard={(file) => {
                  // Repurposed: opens the file detail drawer (the canonical
                  // per-drawing surface). UnifiedCADView listens for this
                  // event + opens its drawer at Overview tab. The drawer is
                  // URL-shareable so ?drawing=<id> mirrors this action.
                  window.dispatchEvent(new CustomEvent('eis:cad:open-drawer', {
                    detail: { file_id: file?.id, initial_tab: 0 },
                  }));
                }}
                // Bulk-group handlers — dispatch with all checkboxed file IDs.
                // UnifiedCADView iterates, resolves files → drawings, and
                // calls the API for each in parallel.
                onBulkExtract={() => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action-bulk', {
                    detail: { file_ids: cadSelectedFileIds, action: 'extract-2d' },
                  }));
                }}
                onBulkSubmit={() => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action-bulk', {
                    detail: { file_ids: cadSelectedFileIds, action: 'submit-review' },
                  }));
                }}
                onBulkTag={() => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bom-action-bulk', {
                    detail: { file_ids: cadSelectedFileIds, action: 'tag' },
                  }));
                }}
                onBulkClear={() => {
                  window.dispatchEvent(new CustomEvent('eis:cad:bulk-clear'));
                }}
              />
            </Box>
          )}
          {/* Electronics (ECAD) ribbon — dedicated minimal ribbon (Link /
              Suggest Links / New Version). BOM import is deferred until the
              ECAD-BOM machinery merges from the cross-domain branch. */}
          {activeSection === 'electronics' && (
            <Box sx={{ position: 'sticky', top: 80, zIndex: 1100 }}>
              <EcadRibbon
                selectedFile={electronicsSelectedFile}
                onLink={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:ecad:link-entity', {
                    detail: { file_id: file?.id },
                  }));
                }}
                onSuggestLinks={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:ecad:suggest-links', {
                    detail: { file_id: file?.id },
                  }));
                }}
                onNewVersion={(file) => {
                  window.dispatchEvent(new CustomEvent('eis:ecad:new-version', {
                    detail: { file_id: file?.id },
                  }));
                }}
              />
            </Box>
          )}
          <Box sx={{ p: 3 }}>
          <Container maxWidth={false}>

            {/* Breadcrumbs — only shown on the projects list view (not inside a project) */}
            {activeSection === 'projects' && !selectedProject && (
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {getBreadcrumbs().map((crumb, index) => {
                    const isLast = index === getBreadcrumbs().length - 1;
                    return isLast ? (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    ) : (
                        <Link
                        key={index}
                        underline="hover"
                        color="inherit"
                        onClick={(e) => {
                            e.preventDefault();
                            if (crumb.onClick) crumb.onClick();
                        }}
                        href="#"
                        >
                        {crumb.label}
                        </Link>
                    );
                })}
                </Breadcrumbs>
            )}

            {/*
             * Two-column grid layout:
             *   Left (1fr)   — Main section content (projects, CAD, etc.)
             *   Right (400px) — Chat sidebar (always visible in authenticated view)
             *
             * On smaller screens (below md breakpoint), this collapses to a single column.
             */}
            <Box sx={{
              display: 'grid',
              gap: chatCollapsed ? 0 : 3,
              gridTemplateColumns: {
                md: activeSection === 'orchestrator' || chatCollapsed
                  ? '1fr'
                  : '1fr 480px',
              },
            }}>
              {/* ===== Left Column: Section Content ===== */}
              <Suspense fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress sx={{ color: '#667eea' }} />
                </Box>
              }>
              <Box>

                {/*
                 * Projects List — shown when activeSection is 'projects' and no project is selected.
                 * ProjectDashboard handles its own data fetching; receives currentUser and users
                 * for member assignment. onProjectSelect triggers handleProjectChange which
                 * sets selectedProject, persists to localStorage, and updates recent list.
                 */}
                {activeSection === 'projects' && !selectedProject && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ProjectDashboard
                            onProjectSelect={(project) => {
                                // Opening a product lands on the project workspace
                                // (ProjectView — branch/member detail). V&V Overview
                                // stays reachable from the left nav.
                                handleProjectChange(project);
                            }}
                            currentUser={currentUser}
                            onProjectsChanged={refreshAllProjects}
                        />
                    </Paper>
                )}

                {/*
                 * Project Detail View — shown when a specific project is selected.
                 * Receives branches and branch management callbacks so the user can
                 * create/switch/delete branches within this view.
                 *
                 * Data flow:
                 *   selectedProject -> ProjectView (displays project details)
                 *   onProjectUpdate -> updates selectedProject (e.g. after editing project name)
                 *   onBranchChange  -> setCurrentBranch (propagates to all engineering views)
                 *   onBranchesRefresh -> refreshBranches (re-fetches branch list from API)
                 */}
                {activeSection === 'projects' && selectedProject && (
                    <>
                    <Button
                      startIcon={<ArrowBack />}
                      onClick={() => setSelectedProject(null)}
                      sx={{
                        mb: 2,
                        color: '#667eea',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: 'rgba(102, 126, 234, 0.08)',
                        },
                      }}
                    >
                      Back to Products
                    </Button>
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ProjectView
                            project={selectedProject}
                            currentUser={currentUser}
                            users={users}
                            onProjectUpdate={(updatedProject) => {
                              setSelectedProject(updatedProject);
                              refreshAllProjects();
                            }}
                            currentBranchId={currentBranch}
                            onBranchChange={setCurrentBranch}
                            branches={branches}
                            onBranchesRefresh={refreshBranches}
                        />
                    </Paper>
                    </>
                )}

                {/*
                 * Engineering Views — each renders when its activeSection matches.
                 * All receive projectId and branchId so they can scope their data.
                 *
                 * NOTE: projectId is optional (uses ?. operator). These views work
                 * without a selected project, showing either global data or an empty state.
                 *
                 * WARNING: If you add a new section view, also add its key to:
                 *   1. GlobalNavigation's section list
                 *   2. getBreadcrumbs().sectionLabels
                 *   3. The conditional render block here
                 */}
                {activeSection === 'cad' && (
                    <UnifiedCADView
                        projectId={selectedProject?.project_id}
                        branchId={currentBranch}
                        onActiveDrawingChange={setActiveDrawingId}
                        onSelectedDrawingChange={setCadSelectedDrawing}
                        onSelectedFileChange={setCadSelectedFile}
                        onSelectedFileIdsChange={setCadSelectedFileIds}
                    />
                )}
                {activeSection === 'electronics' && (
                    <ElectronicsView
                        projectId={selectedProject?.project_id}
                        onSelectedFileChange={setElectronicsSelectedFile}
                        onSectionChange={handleSectionChange}
                    />
                )}
                {activeSection === 'documents' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <DocumentsView projectId={selectedProject?.project_id} branchId={currentBranch} onSectionChange={handleSectionChange} initialTab={navRequestedTabs.documents ?? 0} createSignal={createSignals.documents ?? 0} />
                    </Paper>
                )}
                {activeSection === 'requirements' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <RequirementsView projectId={selectedProject?.project_id} branchId={currentBranch} onSectionChange={handleSectionChange} initialTab={navRequestedTabs.requirements ?? 0} createSignal={createSignals.requirements ?? 0} />
                    </Paper>
                )}
                {activeSection === 'compliance' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <ComplianceView projectId={selectedProject?.project_id} branchId={currentBranch} onSectionChange={handleSectionChange} initialTab={navRequestedTabs.compliance ?? 0} initialSubTab={navRequestedSubTabs.compliance ?? 0} createSignal={createSignals.compliance ?? 0} focusEntity={complianceFocus} />
                    </Paper>
                )}
                {activeSection === 'declaration-of-conformity' && (
                    <Paper elevation={2} sx={{ mb: 3, p: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <DeclarationOfConformityView projectId={selectedProject?.project_id} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'validation' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <ValidationFindingsView projectId={selectedProject?.project_id} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'vv' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <VVRunnerView projectId={selectedProject?.project_id} branchId={currentBranch} onSectionChange={handleSectionChange} initialTab={navRequestedTabs.vv ?? 0} createSignal={createSignals.vv ?? 0} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'quality-events' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <QualityManagementView projectId={selectedProject?.project_id} branchId={currentBranch} onSectionChange={handleSectionChange} initialTab={navRequestedTabs['quality-events'] ?? 0} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'knowledge-base' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <KnowledgeBaseView projectId={selectedProject?.project_id} branchId={currentBranch} />
                    </Paper>
                )}
                {activeSection === 'knowledge-graph' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <ChangeImpactView
                              projectId={selectedProject?.project_id}
                              onSectionChange={handleSectionChange}
                              focusRequest={kgFocusRequest}
                              onFocusRequestHandled={() => setKgFocusRequest(null)}
                            />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'pl-calculator' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <PLCalculatorView projectId={selectedProject?.project_id} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'analytics' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <AnalyticsView projectId={selectedProject?.project_id} />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'integrations' && (
                    <Paper elevation={2} sx={{ mb: 3 }}>
                        <Suspense fallback={<Box p={4} display="flex" justifyContent="center"><CircularProgress /></Box>}>
                            <IntegrationsView />
                        </Suspense>
                    </Paper>
                )}
                {activeSection === 'orchestrator' && (
                    <OrchestratorView projectId={selectedProject?.project_id} branchId={currentBranch} />
                )}

                {/*
                 * CAD Viewer — full-featured 3D viewer.
                 * Unlike other views, also receives sessionId and userId because
                 * it integrates with the chat/agent system for CAD operations.
                 */}
                {activeSection === 'cadviewer' && (
                    <CADViewerView
                        sessionId={sessionId}
                        projectId={selectedProject?.project_id}
                        branchId={currentBranch}
                        userId={currentUser?.id || user?.user_id}
                    />
                )}
              </Box>
              </Suspense>

              {/*
               * ===== Right Column: Chat Sidebar =====
               * Always rendered in the authenticated view regardless of active section.
               * Receives the current context (branch, project, user) so AI responses
               * are scoped to what the user is working on.
               *
               * Data flow:
               *   sessionId      -> ties messages to this browser session
               *   uploadedFiles  -> provides file context to the AI
               *   activeSection  -> lets the AI know what view the user is looking at
               *   context object -> branch_id, project_id, user_id for scoped queries
               */}
              {chatCollapsed && activeSection !== 'orchestrator' && (
                <Tooltip title="Open e-pilot" placement="left">
                  <Fab
                    size="medium"
                    onClick={() => setChatCollapsed(false)}
                    sx={{
                      position: 'fixed',
                      bottom: 32,
                      right: 24,
                      zIndex: 1300,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      boxShadow: '0 4px 20px rgba(102,126,234,0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      },
                    }}
                  >
                    <AutoAwesome />
                  </Fab>
                </Tooltip>
              )}
              {!chatCollapsed && (
                <Paper data-tour="chat-container" elevation={2} sx={{ p: 0, height: 'calc(100vh - 88px)', display: activeSection === 'orchestrator' ? 'none' : 'flex', flexDirection: 'column', overflow: 'hidden', position: 'sticky', top: 80 }}>
                  {/* Floating collapse handle — top-left of chat. Doesn't
                      touch ChatContainer internals; renders above its
                      header via absolute positioning. */}
                  <Tooltip title="Hide e-pilot chat" placement="left">
                    <IconButton
                      size="small"
                      onClick={() => setChatCollapsed(true)}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        left: 6,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <ChatContainer
                      sessionId={sessionId}
                      uploadedFiles={uploadedFiles}
                      activeSection={activeSection}
                      context={{
                          branch_id: currentBranch,
                          project_id: selectedProject?.project_id,
                          user_id: currentUser?.id || user?.user_id,
                          drawing_id: activeDrawingId,
                          active_section: activeSection
                      }}
                  />
                </Paper>
              )}
            </Box>
          </Container>
          </Box>
        </Box>

      <BottomActivityPanel projectId={selectedProject?.project_id} activeSection={activeSection} />
      </Box>
      <WelcomeDialog onCreateProject={() => {
        setActiveSection('projects');
        setSelectedProject(null);
      }} />
      <OnboardingChecklist onNavigate={(section) => {
        setActiveSection(section);
        if (section === 'projects') setSelectedProject(null);
      }} />
      <SpotlightGuide />
      <ProjectTutorial />

      {/* Header-launched dialogs (Help › Shortcuts, Help › About) */}
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
      {/* CAD ribbon Compare-group dialog */}
      <WhatChangedDialog
        open={!!whatChangedDrawing}
        onClose={() => setWhatChangedDrawing(null)}
        drawing={whatChangedDrawing}
      />
      {/* Upload progress panel — persists across section navigation */}
      <GlobalUploadPanel />
    </JobPollerProvider>
    </ReleaseProvider>
  );
}

export default App;
